import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BadgeIcon from "@mui/icons-material/Badge";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LockIcon from "@mui/icons-material/Lock";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditIcon from "@mui/icons-material/Edit";

import { useDispatch, useSelector } from "react-redux";
import { api, getSupabase } from "common";

export default function UserCreateDialog({
  open,
  onClose,
  onSuccess,
  defaultRole = "driver",
  userToEdit = null,
}) {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const { createUserWithAuth, updateUserWithAuth, fetchUsersOnce } = api;

  const isEdit = Boolean(userToEdit);

  const isSuperAdmin =
    auth.profile?.is_superadmin || auth.profile?.usertype === "superadmin";
  const currentTenantId =
    auth.profile?.tenant_id || auth.profile?.tenantId || null;
  const currentTenantName =
    auth.profile?.tenantName || auth.profile?.tenant_name || "Alpha Giro";

  // Estados do formulário
  const [usertype, setUsertype] = useState(defaultRole);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [autoGeneratePass, setAutoGeneratePass] = useState(true);

  // Tenant
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(
    currentTenantId || "",
  );

  // Dados de Motorista
  const [cnh, setCnh] = useState("");
  const [cnhCategory, setCnhCategory] = useState("B");
  const [cnhExpiry, setCnhExpiry] = useState("");

  // Arquivos / Documentos
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [licenseFrontFile, setLicenseFrontFile] = useState(null);
  const [licenseFrontPreview, setLicenseFrontPreview] = useState(null);
  const [licenseBackFile, setLicenseBackFile] = useState(null);
  const [licenseBackPreview, setLicenseBackPreview] = useState(null);
  const [verifyDocFile, setVerifyDocFile] = useState(null);
  const [verifyDocPreview, setVerifyDocPreview] = useState(null);

  // Controle de carregamento e mensagens
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successResult, setSuccessResult] = useState(null);

  // Carregar lista de tenants e preencher dados em caso de edição
  useEffect(() => {
    if (open) {
      setErrorMsg("");
      setSuccessResult(null);

      if (userToEdit) {
        setUsertype(userToEdit.usertype || defaultRole);
        setFirstName(userToEdit.firstName || userToEdit.first_name || "");
        setLastName(userToEdit.lastName || userToEdit.last_name || "");
        setEmail(userToEdit.email || "");
        setMobile(userToEdit.mobile || "");
        setSelectedTenantId(userToEdit.tenant_id || userToEdit.tenantId || currentTenantId || "");
        setCnh(userToEdit.cnh || "");
        setCnhCategory(userToEdit.cnh_category || userToEdit.cnhCategory || "B");
        setCnhExpiry(userToEdit.cnh_expiry || userToEdit.cnhExpiry || "");
        setProfileImagePreview(userToEdit.profile_image || null);
        setLicenseFrontPreview(userToEdit.license_image || userToEdit.licenseImage || null);
        setLicenseBackPreview(userToEdit.license_image_back || userToEdit.licenseImageBack || null);
        setVerifyDocPreview(userToEdit.verify_id_image || userToEdit.verifyIdImage || null);
        setProfileImageFile(null);
        setLicenseFrontFile(null);
        setLicenseBackFile(null);
        setVerifyDocFile(null);
      } else {
        setUsertype(defaultRole);
        resetForm();
      }

      const supabase = getSupabase?.();
      if (supabase) {
        supabase
          .from("tenants")
          .select("id, name, slug, status")
          .order("name", { ascending: true })
          .then(({ data, error }) => {
            if (error) {
              console.error(
                "[UserCreateDialog] Erro ao carregar tenants:",
                error,
              );
            }
            if (data) {
              setTenants(data);
              if (data.length > 0 && !selectedTenantId) {
                const targetTenant = userToEdit ? (userToEdit.tenant_id || userToEdit.tenantId) : currentTenantId;
                const matched = targetTenant && data.find((t) => t.id === targetTenant);
                setSelectedTenantId(matched ? matched.id : data[0].id);
              }
            }
          });
      } else {
        setSelectedTenantId(currentTenantId || "");
      }
    }
  }, [open, defaultRole, userToEdit, currentTenantId]);

  // Handlers para preview de imagens
  const handleFileChange = (e, setFile, setPreview) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const removeFile = (setFile, setPreview) => {
    setFile(null);
    setPreview(null);
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setMobile("");
    setPassword("");
    setAutoGeneratePass(true);
    setCnh("");
    setCnhCategory("B");
    setCnhExpiry("");
    setProfileImageFile(null);
    setProfileImagePreview(null);
    setLicenseFrontFile(null);
    setLicenseFrontPreview(null);
    setLicenseBackFile(null);
    setLicenseBackPreview(null);
    setVerifyDocFile(null);
    setVerifyDocPreview(null);
    setErrorMsg("");
    setSuccessResult(null);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!firstName.trim()) {
      setErrorMsg("Informe o nome do usuário.");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setErrorMsg("Informe um e-mail válido.");
      return;
    }

    setLoading(true);

    try {
      if (isEdit) {
        const updatePayload = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          mobile: mobile.trim(),
          usertype,
          tenant_id: isSuperAdmin ? selectedTenantId : currentTenantId,
          cnh: cnh.trim(),
          cnh_category: cnhCategory,
          cnh_expiry: cnhExpiry || null,
          profile_image: profileImagePreview,
          license_image: licenseFrontPreview,
          license_image_back: licenseBackPreview,
          verify_id_image: verifyDocPreview,
          profile_image_file: profileImageFile,
          license_image_file: licenseFrontFile,
          license_image_back_file: licenseBackFile,
          verify_id_image_file: verifyDocFile,
        };

        const result = await dispatch(updateUserWithAuth(userToEdit.id, updatePayload));
        if (result && result.success) {
          dispatch(fetchUsersOnce());
          if (onSuccess) onSuccess();
          onClose();
        } else {
          setErrorMsg(result?.error || "Não foi possível salvar as alterações do usuário.");
        }
      } else {
        const payload = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim().toLowerCase(),
          mobile: mobile.trim(),
          usertype,
          tenant_id: isSuperAdmin ? selectedTenantId : currentTenantId,
          password: autoGeneratePass ? undefined : password.trim(),
          cnh: cnh.trim(),
          cnh_category: cnhCategory,
          cnh_expiry: cnhExpiry || null,
          profile_image_file: profileImageFile,
          license_image_file: licenseFrontFile,
          license_image_back_file: licenseBackFile,
          verify_id_image_file: verifyDocFile,
        };

        const result = await dispatch(createUserWithAuth(payload));

        if (result.success) {
          setSuccessResult({
            email: payload.email,
            generatedPassword: result.generatedPassword,
            name: `${payload.firstName} ${payload.lastName}`.trim(),
          });
          dispatch(fetchUsersOnce());
          if (onSuccess) onSuccess();
        } else {
          setErrorMsg(result.error || "Não foi possível cadastrar o usuário.");
        }
      }
    } catch (err) {
      setErrorMsg(err.message || "Erro inesperado ao salvar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          bgcolor: "#ffffff",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2.5,
          bgcolor: "#0f172a",
          color: "#ffffff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {isEdit ? (
            <EditIcon sx={{ color: "#38bdf8", fontSize: 28 }} />
          ) : (
            <PersonIcon sx={{ color: "#38bdf8", fontSize: 28 }} />
          )}
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "1.15rem" }}
            >
              {isEdit ? `Editar Usuário: ${firstName} ${lastName}`.trim() : "Novo Cadastro de Usuário"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              {isEdit
                ? "Atualização de dados cadastrais, documentos e permissões operacionais"
                : "Integração direta com Supabase Auth e perfis operacionais"}
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: "#94a3b8",
            "&:hover": { color: "#ffffff", bgcolor: "rgba(255,255,255,0.1)" },
          }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent
          sx={{ p: 3, maxHeight: "calc(80vh - 120px)", overflowY: "auto" }}
        >
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: "8px" }}>
              {errorMsg}
            </Alert>
          )}

          {successResult ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: 64, color: "#10b981", mb: 1.5 }}
              />
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}
              >
                Usuário cadastrado com sucesso!
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
                O acesso foi criado no Supabase Auth para{" "}
                <strong>{successResult.email}</strong>.
              </Typography>

              {successResult.generatedPassword ? (
                <Box
                  sx={{
                    bgcolor: "#f8fafc",
                    p: 2,
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    maxWidth: 400,
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", display: "block" }}
                  >
                    Senha temporária gerada:
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: 700,
                      color: "#0284c7",
                      letterSpacing: "1px",
                    }}
                  >
                    {successResult.generatedPassword}
                  </Typography>
                </Box>
              ) : null}

              <Alert
                severity="info"
                sx={{ maxWidth: 500, mx: "auto", textAlign: "left", mb: 2 }}
              >
                A confirmação será enviada para o e-mail informado.
              </Alert>

              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  bgcolor: "#0f172a",
                  color: "#ffffff",
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#1e293b" },
                }}
              >
                Concluir
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2.5}>
              {/* 1. SELEÇÃO DE PERFIL E EMPRESA */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "#475569",
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: "0.5px",
                    mb: 1,
                  }}
                >
                  1. Perfil de Acesso & Frota
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="usertype-label">Tipo de Usuário *</InputLabel>
                  <Select
                    labelId="usertype-label"
                    value={usertype}
                    label="Tipo de Usuário *"
                    onChange={(e) => setUsertype(e.target.value)}
                  >
                    {isSuperAdmin ? [
                      <MenuItem key="fleetadmin" value="fleetadmin">
                        Gestor de Frota (Painel da Empresa)
                      </MenuItem>,
                      <MenuItem key="admin" value="admin">
                        Administrador Geral
                      </MenuItem>,
                      <MenuItem key="driver" value="driver">
                        Condutor / Motorista (App & Rotas)
                      </MenuItem>,
                      <MenuItem key="customer" value="customer">
                        Solicitante / Passageiro
                      </MenuItem>
                    ] : [
                      <MenuItem key="driver" value="driver">
                        Condutor / Motorista (App & Rotas)
                      </MenuItem>,
                      <MenuItem key="customer" value="customer">
                        Solicitante / Passageiro
                      </MenuItem>,
                      <MenuItem key="fleetadmin" value="fleetadmin">
                        Gestor de Frota (Mesma Frota)
                      </MenuItem>
                    ]}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="tenant-label">
                    Empresa / Frota (Tenant)
                  </InputLabel>
                  <Select
                    labelId="tenant-label"
                    value={!isSuperAdmin ? (currentTenantId || "") : (selectedTenantId || "")}
                    label="Empresa / Frota (Tenant)"
                    onChange={(e) => setSelectedTenantId(e.target.value)}
                    disabled={!isSuperAdmin}
                  >
                    {isSuperAdmin && (
                      <MenuItem value="">
                        <em>Nenhuma (Acesso Global / Sem Vínculo)</em>
                      </MenuItem>
                    )}
                    {tenants.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name} {t.slug ? `(${t.slug})` : ""}
                      </MenuItem>
                    ))}
                    {!isSuperAdmin && tenants.length === 0 && currentTenantId && (
                      <MenuItem value={currentTenantId}>
                        {currentTenantName || "Minha Empresa"}
                      </MenuItem>
                    )}
                    {tenants.length === 0 && isSuperAdmin && (
                      <MenuItem value="" disabled>
                        Nenhuma empresa cadastrada (crie em Empresas)
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {/* 2. DADOS PESSOAIS */}
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "#475569",
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: "0.5px",
                    mb: 1,
                  }}
                >
                  2. Informações Pessoais & Credenciais
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Primeiro Nome *"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Ex: Carlos"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Sobrenome"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Ex: Silva"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label="E-mail de Acesso *"
                  value={email}
                  disabled={isEdit}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="usuario@alphagiro.com.br"
                  helperText={
                    isEdit
                      ? "Identificador da conta Supabase Auth"
                      : "Será utilizado para autenticação no Supabase Auth"
                  }
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Telefone / WhatsApp"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="(31) 99999-9999"
                />
              </Grid>

              {!isEdit && (
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      Senha de acesso:
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setAutoGeneratePass(!autoGeneratePass)}
                      sx={{ textTransform: "none", fontSize: "0.75rem", p: 0 }}
                    >
                      {autoGeneratePass
                        ? "Digitar senha manual"
                        : "Gerar automática"}
                    </Button>
                  </Box>
                  {autoGeneratePass ? (
                    <TextField
                      fullWidth
                      size="small"
                      disabled
                      value="Será gerada e exibida após cadastro"
                      InputProps={{
                        startAdornment: (
                          <LockIcon
                            sx={{ color: "#94a3b8", mr: 1, fontSize: 18 }}
                          />
                        ),
                      }}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      type="password"
                      label="Senha Provisória"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                    />
                  )}
                </Grid>
              )}

              {/* 3. DADOS DE MOTORISTA / CONDUTOR (SE HABILITADO) */}
              {usertype === "driver" && (
                <>
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <DirectionsCarIcon
                        sx={{ color: "#0284c7", fontSize: 20 }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: "#475569",
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                          letterSpacing: "0.5px",
                        }}
                      >
                        3. Habilitação & Dados de Condução
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Registro CNH"
                      value={cnh}
                      onChange={(e) => setCnh(e.target.value)}
                      placeholder="00000000000"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="cnh-cat-label">Categoria CNH</InputLabel>
                      <Select
                        labelId="cnh-cat-label"
                        value={cnhCategory}
                        label="Categoria CNH"
                        onChange={(e) => setCnhCategory(e.target.value)}
                      >
                        <MenuItem value="A">A (Moto)</MenuItem>
                        <MenuItem value="B">B (Carro)</MenuItem>
                        <MenuItem value="AB">AB (Carro e Moto)</MenuItem>
                        <MenuItem value="C">C (Caminhão leve)</MenuItem>
                        <MenuItem value="D">D (Vans / Ônibus)</MenuItem>
                        <MenuItem value="E">E (Veículos articulados)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Validade CNH"
                      value={cnhExpiry}
                      onChange={(e) => setCnhExpiry(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}

              {/* 4. UPLOADS DE IMAGENS E DOCUMENTOS (OPCIONAIS) */}
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: "#475569",
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      letterSpacing: "0.5px",
                    }}
                  >
                    4. Fotos & Documentos (Opcional)
                  </Typography>
                  <Chip
                    label="Opcional no momento"
                    size="small"
                    sx={{
                      bgcolor: "#f1f5f9",
                      color: "#64748b",
                      fontSize: "0.7rem",
                    }}
                  />
                </Box>
              </Grid>

              {/* Upload Foto de Perfil */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    border: "1px dashed #cbd5e1",
                    borderRadius: "10px",
                    p: 2,
                    textAlign: "center",
                    bgcolor: profileImagePreview ? "#f0fdf4" : "#f8fafc",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                    }}
                  >
                    <Avatar
                      src={profileImagePreview}
                      sx={{ width: 48, height: 48, bgcolor: "#e2e8f0" }}
                    >
                      <PersonIcon sx={{ color: "#94a3b8" }} />
                    </Avatar>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", fontWeight: 600, color: "#334155" }}
                  >
                    Foto de Perfil / Avatar
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    id="profile-img-upload"
                    style={{ display: "none" }}
                    onChange={(e) =>
                      handleFileChange(
                        e,
                        setProfileImageFile,
                        setProfileImagePreview,
                      )
                    }
                  />
                  <Box sx={{ mt: 1 }}>
                    <label htmlFor="profile-img-upload">
                      <Button
                        component="span"
                        size="small"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                      >
                        {profileImagePreview ? "Trocar Imagem" : "Selecionar"}
                      </Button>
                    </label>
                    {profileImagePreview && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          removeFile(
                            setProfileImageFile,
                            setProfileImagePreview,
                          )
                        }
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          ml: 1,
                        }}
                      >
                        Remover
                      </Button>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Upload CNH Frente */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    border: "1px dashed #cbd5e1",
                    borderRadius: "10px",
                    p: 2,
                    textAlign: "center",
                    bgcolor: licenseFrontPreview ? "#f0fdf4" : "#f8fafc",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                    }}
                  >
                    <Avatar
                      src={licenseFrontPreview}
                      variant="rounded"
                      sx={{ width: 48, height: 48, bgcolor: "#e2e8f0" }}
                    >
                      <BadgeIcon sx={{ color: "#94a3b8" }} />
                    </Avatar>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", fontWeight: 600, color: "#334155" }}
                  >
                    CNH / Documento (Frente)
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    id="cnh-front-upload"
                    style={{ display: "none" }}
                    onChange={(e) =>
                      handleFileChange(
                        e,
                        setLicenseFrontFile,
                        setLicenseFrontPreview,
                      )
                    }
                  />
                  <Box sx={{ mt: 1 }}>
                    <label htmlFor="cnh-front-upload">
                      <Button
                        component="span"
                        size="small"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                      >
                        {licenseFrontPreview ? "Trocar" : "Selecionar"}
                      </Button>
                    </label>
                    {licenseFrontPreview && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          removeFile(
                            setLicenseFrontFile,
                            setLicenseFrontPreview,
                          )
                        }
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          ml: 1,
                        }}
                      >
                        Remover
                      </Button>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Upload CNH Verso */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    border: "1px dashed #cbd5e1",
                    borderRadius: "10px",
                    p: 2,
                    textAlign: "center",
                    bgcolor: licenseBackPreview ? "#f0fdf4" : "#f8fafc",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                    }}
                  >
                    <Avatar
                      src={licenseBackPreview}
                      variant="rounded"
                      sx={{ width: 48, height: 48, bgcolor: "#e2e8f0" }}
                    >
                      <BadgeIcon sx={{ color: "#94a3b8" }} />
                    </Avatar>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", fontWeight: 600, color: "#334155" }}
                  >
                    CNH / Documento (Verso)
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    id="cnh-back-upload"
                    style={{ display: "none" }}
                    onChange={(e) =>
                      handleFileChange(
                        e,
                        setLicenseBackFile,
                        setLicenseBackPreview,
                      )
                    }
                  />
                  <Box sx={{ mt: 1 }}>
                    <label htmlFor="cnh-back-upload">
                      <Button
                        component="span"
                        size="small"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                      >
                        {licenseBackPreview ? "Trocar" : "Selecionar"}
                      </Button>
                    </label>
                    {licenseBackPreview && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          removeFile(setLicenseBackFile, setLicenseBackPreview)
                        }
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          ml: 1,
                        }}
                      >
                        Remover
                      </Button>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Upload Comprovante / Doc Adicional */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    border: "1px dashed #cbd5e1",
                    borderRadius: "10px",
                    p: 2,
                    textAlign: "center",
                    bgcolor: verifyDocPreview ? "#f0fdf4" : "#f8fafc",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                    }}
                  >
                    <Avatar
                      src={verifyDocPreview}
                      variant="rounded"
                      sx={{ width: 48, height: 48, bgcolor: "#e2e8f0" }}
                    >
                      <BadgeIcon sx={{ color: "#94a3b8" }} />
                    </Avatar>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", fontWeight: 600, color: "#334155" }}
                  >
                    Comprovante / Doc Adicional
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    id="verify-doc-upload"
                    style={{ display: "none" }}
                    onChange={(e) =>
                      handleFileChange(e, setVerifyDocFile, setVerifyDocPreview)
                    }
                  />
                  <Box sx={{ mt: 1 }}>
                    <label htmlFor="verify-doc-upload">
                      <Button
                        component="span"
                        size="small"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                      >
                        {verifyDocPreview ? "Trocar" : "Selecionar"}
                      </Button>
                    </label>
                    {verifyDocPreview && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          removeFile(setVerifyDocFile, setVerifyDocPreview)
                        }
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          ml: 1,
                        }}
                      >
                        Remover
                      </Button>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        {!successResult && (
          <DialogActions
            sx={{ p: 2.5, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}
          >
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{ color: "#64748b" }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={{
                bgcolor: "#0f172a",
                color: "#ffffff",
                fontWeight: 600,
                px: 3,
                borderRadius: "8px",
                "&:hover": { bgcolor: "#1e293b" },
              }}
            >
              {loading
                ? isEdit
                  ? "Salvando alterações..."
                  : "Cadastrando..."
                : isEdit
                  ? "Salvar Alterações"
                  : "Cadastrar Usuário"}
            </Button>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
}
